import { useState, useEffect } from 'react';
import { Button } from '@bancobarigui/barihaus';
import { CalendarOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { message } from 'antd';

import { InspectionService } from '@/services/inspection.service';
import { useAuth } from '@/contexts/auth.context';

interface GoogleCalendarButtonProps {
  className?: string;
}

export function GoogleCalendarButton({ className }: GoogleCalendarButtonProps) {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  const inspectionService = new InspectionService();

  useEffect(() => {
    checkConnectionStatus();
    
    const urlParams = new URLSearchParams(window.location.search);
    const calendarStatus = urlParams.get('calendar');
    
    if (calendarStatus === 'connected') {
      message.success('Google Calendar conectado com sucesso!');
      setIsConnected(true);
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } else if (calendarStatus === 'error') {
      const errorMessage = urlParams.get('message') || 'Erro ao conectar Google Calendar';
      message.error(decodeURIComponent(errorMessage));
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [user]);

  const checkConnectionStatus = async () => {
    if (!user?.id) return;

    try {
      setIsCheckingStatus(true);
      const status = await inspectionService.getGoogleCalendarStatus(user.id);
      setIsConnected(status.isConnected);
    } catch {
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleConnect = async () => {
    if (!user?.id) {
      message.error('Usuário não identificado');
      return;
    }

    try {
      setIsLoading(true);
      const response = await inspectionService.connectGoogleCalendar(user.id);
      
      window.location.href = response.authorizationUrl;

    } catch {
      message.error('Erro ao tentar conectar com o Google Calendar');
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user?.id) {
      message.error('Usuário não identificado');
      return;
    }

    try {
      setIsLoading(true);
      await inspectionService.disconnectGoogleCalendar(user.id);
      setIsConnected(false);
      message.success('Google Calendar desconectado com sucesso!');
    } catch {
      message.error('Erro ao tentar desconectar do Google Calendar');
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingStatus) {
    return (
      <Button 
        disabled 
        icon={<LoadingOutlined />}
        className={className || ''}
      >
        Verificando status...
      </Button>
    );
  }

  if (isConnected) {
    return (
      <Button
        type="default"
        icon={<CheckCircleOutlined />}
        onClick={handleDisconnect}
        loading={isLoading}
        className={`!text-green-600 !border-green-600 hover:!bg-green-50 ${className}`}
      >
        Google Agenda Conectado
      </Button>
    );
  }

  return (
    <Button
      buttonColor="primary"
      icon={<CalendarOutlined />}
      onClick={handleConnect}
      loading={isLoading}
      {...(className ? { className } : {})}
    >
      Conectar Google Agenda
    </Button>
  );
}